import _ from "lodash";

function trackNavScroll(currentNavElement: HTMLDivElement | null) {
  if (currentNavElement === null) {
    return;
  }

  sessionStorage.setItem(
    "navScrollPosition",
    currentNavElement.scrollTop.toString(),
  );
}

export function createScrollHandler(
  scrollContainerRef: React.RefObject<HTMLDivElement>,
) {
  return _.debounce(() => {
    trackNavScroll(scrollContainerRef.current);
  }, 200);
}

export function restoreScrollPosition(
  scrollContainerRef: React.RefObject<HTMLDivElement>,
) {
  if (scrollContainerRef.current) {
    scrollContainerRef.current.scrollTop = parseInt(
      sessionStorage.getItem("navScrollPosition") ?? "0",
      10,
    );
  }
}

export function resetScrollPosition() {
  sessionStorage.setItem("navScrollPosition", "0");
}

interface CategoryScrollGeometry {
  scrollTop: number;
  viewportHeight: number;
  /** Category's offset from the top of the scroll container's content, ignoring scroll position. */
  categoryTop: number;
  /** Header plus fully expanded items, even if the expand animation hasn't finished. */
  categoryHeight: number;
  maxScrollTop: number;
}

export function computeCategoryScrollTop({
  scrollTop,
  viewportHeight,
  categoryTop,
  categoryHeight,
  maxScrollTop,
}: CategoryScrollGeometry): number {
  const clamp = (value: number) =>
    Math.max(0, Math.min(value, Math.max(0, maxScrollTop)));

  // Too tall to ever fit: the header at the top shows as many items as possible.
  if (categoryHeight > viewportHeight) {
    return clamp(categoryTop);
  }

  if (categoryTop < scrollTop) {
    return clamp(categoryTop);
  }

  const categoryBottom = categoryTop + categoryHeight;
  if (categoryBottom > scrollTop + viewportHeight) {
    return clamp(categoryBottom - viewportHeight);
  }

  return clamp(scrollTop);
}

/** Kept in step with MUI's `duration.standard`, which drives the category's Collapse. */
export const NAV_SCROLL_ANIMATION_MS = 300;

/** MUI's `easing.easeInOut`, i.e. cubic-bezier(0.4, 0, 0.2, 1). */
const EASE_IN_OUT = { x1: 0.4, y1: 0, x2: 0.2, y2: 1 };

function bezierAxis(t: number, p1: number, p2: number): number {
  const inverse = 1 - t;
  return 3 * inverse * inverse * t * p1 + 3 * inverse * t * t * p2 + t ** 3;
}

function easeInOut(progress: number): number {
  let lower = 0;
  let upper = 1;
  let t = progress;
  // Binary search for the bezier parameter whose x equals `progress`.
  for (let i = 0; i < 12; i++) {
    if (bezierAxis(t, EASE_IN_OUT.x1, EASE_IN_OUT.x2) < progress) {
      lower = t;
    } else {
      upper = t;
    }
    t = (lower + upper) / 2;
  }
  return bezierAxis(t, EASE_IN_OUT.y1, EASE_IN_OUT.y2);
}

interface ScrollTopTarget {
  scrollTop: number;
}

/**
 * Tweens `scrollTop` rather than using `scrollTo({ behavior: "smooth" })`: the
 * target often lies past the container's current scrollable range because the
 * category is still expanding into it. Writing the eased position every frame
 * lets the browser clamp each write and lets the scroll catch up as the
 * container grows.
 *
 * Returns a function that abandons the animation wherever it has got to.
 */
export function animateScrollTop(
  element: ScrollTopTarget,
  targetTop: number,
  durationMs: number = NAV_SCROLL_ANIMATION_MS,
): () => void {
  const prefersReducedMotion =
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

  if (prefersReducedMotion || durationMs <= 0) {
    element.scrollTop = targetTop;
    return () => {};
  }

  const startTop = element.scrollTop;
  const startTime = performance.now();
  let frame: number | null = null;

  const step = () => {
    const progress = Math.min((performance.now() - startTime) / durationMs, 1);

    if (progress >= 1) {
      // The easing solver only approximates, so land on the target exactly.
      element.scrollTop = targetTop;
      frame = null;
      return;
    }

    element.scrollTop = startTop + (targetTop - startTop) * easeInOut(progress);
    frame = requestAnimationFrame(step);
  };

  frame = requestAnimationFrame(step);

  return () => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
  };
}
